import {
  Button,
  HorizontalDivider,
  Icon,
  Input,
  Modal,
  Typography
} from '@smartgift/frontend-toolkit';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { MAX_EMAIL_LENGTH, MAX_FIRST_NAME_LENGTH, MAX_LAST_NAME_LENGTH } from '@hero/constants';
import { useRecognitionSharePreview } from '@hero/services/order-service/recognition/hooks';
import { addNewContactSchema } from '@hero/validations';

import MemberItem from './MemberItem';
import * as S from './style';

function ShareGiftPreviewModal({ isModalOpen, onModalOpen, onBack }) {
  const { t } = useTranslation();
  const { recognitionCode } = useParams();
  const {
    mutateAsync: recognitionSharePreview,
    isSuccess: isSuccessSharePreview,
    isLoading: isFetchingSharePreview
  } = useRecognitionSharePreview({
    recognitionCode
  });

  const [teamMembers, setTeamMembers] = useState([]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      firstName: '',
      lastName: '',
      email: ''
    },
    validationSchema: addNewContactSchema,
    onSubmit: values => {
      setTeamMembers([
        ...teamMembers,
        {
          firstName: values?.firstName,
          lastName: values?.lastName,
          email: values?.email
        }
      ]);
      formik.resetForm();
    }
  });

  const onRemoveMember = email => {
    setTeamMembers(teamMembers.filter(item => item.email !== email));
  };

  const _onSubmitForm = async () => {
    const params = {
      recipients: teamMembers
    };
    try {
      await recognitionSharePreview(params);
    } catch {
      onModalOpen(false);
    }

    setTeamMembers([]);
  };

  const content = () => {
    if (teamMembers.length === 0 && isSuccessSharePreview)
      return (
        <S.SuccessfulContentWrapper>
          <S.SuccessIconWrapper>
            <Icon name="success" size="xxl" color="var(--color-success-default)" />
          </S.SuccessIconWrapper>
          <Typography variant="body2">{t('giftPreviewShared')}</Typography>
        </S.SuccessfulContentWrapper>
      );
    if (teamMembers.length) {
      return (
        <S.MemberContentWrapper>
          {teamMembers.map(item => (
            <MemberItem {...item} key={item.email} onRemove={() => onRemoveMember(item.email)} />
          ))}
        </S.MemberContentWrapper>
      );
    }
    return <S.EmptyContentWrapper>{t('clickAddRecipientAfterNameAndEmail')}</S.EmptyContentWrapper>;
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onRequestClose={() => onModalOpen(false)}
      title={t('shareGiftPreview')}
      onBack={() => {
        onModalOpen(false);
        if (onBack) onBack();
      }}
      primaryButtonProps={{
        children: t('share'),
        onClick: _onSubmitForm,
        disabled: !teamMembers.length || isFetchingSharePreview
      }}
      secondaryButtonProps={{
        children: t('cancel'),
        onClick: () => {
          onModalOpen(false);
          if (onBack) onBack();
        }
      }}
    >
      <S.ShareGiftPreviewWrapper>
        <S.Col1>
          <S.Form onSubmit={formik.handleSubmit}>
            <S.FormInputWrapper>
              <Input
                name="firstName"
                label={t('firstName')}
                variant="outlined"
                size="lg"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                inputProps={{
                  maxLength: MAX_FIRST_NAME_LENGTH
                }}
              />
              <Input
                name="lastName"
                label={t('lastName')}
                variant="outlined"
                size="lg"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                inputProps={{
                  maxLength: MAX_LAST_NAME_LENGTH
                }}
              />
              <Input
                name="email"
                label={t('email')}
                variant="outlined"
                size="lg"
                value={formik.values.email}
                onChange={formik.handleChange}
                inputProps={{
                  maxLength: MAX_EMAIL_LENGTH
                }}
              />
            </S.FormInputWrapper>
            <Button variant="outlined" size="sm" disabled={!formik.isValid}>
              {t('addTeamMember')}
            </Button>
          </S.Form>
        </S.Col1>
        <S.Col2>
          <Typography variant="body2Bold">{t('teamMembers')}:</Typography>
          <HorizontalDivider $margin="16px 0" />
          <S.ShareGiftPreviewContent>{content()}</S.ShareGiftPreviewContent>
        </S.Col2>
      </S.ShareGiftPreviewWrapper>
    </Modal>
  );
}

export { ShareGiftPreviewModal };
